import type { ComponentProps } from "react"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Skeleton from "@/v2-ui/skeleton"
import cn from "@/v2-ui/utils/utils.cn"
import LayerSublayerEditor from "@/v2-backoffice-client/layer/sublayer/LayerSublayerEditor"
import { LAYER_SUBLAYER_EDIT_ENVIRONMENT } from "@/v2-backoffice-client/layer/sublayer/layer.sublayer.constants"
import { useLayerSublayerQuery } from "@/v2-backoffice-client/layer/sublayer/layer.sublayer.query"
import { readStudioSublayers } from "@/v2-backoffice-client/layer/sublayer/layer.sublayer.types"

export type LayerSublayerManagerProps = ComponentProps<"section"> & {
  layerFeatureCode: string
}

/**
 * The layer's sublayers (the chooser's Underkartlag) as editable rows, read
 * from and written to the authoring catalog's 'dev' row. A publish exports
 * them into the parent's Sublayers array; a promote carries them to prod
 * with the parent. Rows disabled here stay listed, greyed, so a disable is
 * visible and reversible.
 */
function LayerSublayerManager(props: LayerSublayerManagerProps) {
  const { layerFeatureCode, className, ...sectionProps } = props

  const { data, loading, error } = useLayerSublayerQuery({
    variables: {
      input: {
        FeatureCode: layerFeatureCode,
        Environment: LAYER_SUBLAYER_EDIT_ENVIRONMENT,
      },
    },
  })

  const studioLayer = data?.getStudioLayer
  const sublayers = readStudioSublayers(studioLayer?.Sublayers)

  return (
    <section
      className={cn("layer-sublayer-manager flex flex-col gap-2", className)}
      {...sectionProps}
    >
      <div className="flex flex-col">
        <span className="text-sm font-medium">Sublayers</span>
        <span className="text-xs text-muted-foreground">
          Catalog rows under this layer, edited in dev and published with it.
          Each saved sublayer carries the same text editor as the layer: every
          locale, Translate from, AI translate missing. Saves land in the
          translation catalog, show under Features → Review pending, and go live
          with the next translation publish. The WMS scale ladder in
          Configuration is a separate thing and stays on the Configuration tab.
        </span>
      </div>

      {loading ? (
        <Skeleton className="h-24 w-full rounded-md" aria-busy="true" />
      ) : error ? (
        <ErrorMessage error={error} />
      ) : !studioLayer ? (
        <span className="text-xs text-muted-foreground">
          No catalog row in dev for this layer, so there is nothing to hang a
          sublayer on. Create the layer in Layer Studio first.
        </span>
      ) : (
        <>
          {sublayers.map((sublayer) => (
            <LayerSublayerEditor
              key={sublayer.Key}
              parentFeatureCode={layerFeatureCode}
              environment={LAYER_SUBLAYER_EDIT_ENVIRONMENT}
              sublayer={sublayer}
            />
          ))}
          <LayerSublayerEditor
            parentFeatureCode={layerFeatureCode}
            environment={LAYER_SUBLAYER_EDIT_ENVIRONMENT}
            className="border-dashed"
          />
        </>
      )}
    </section>
  )
}

export default LayerSublayerManager
